import { isNullable } from "effect/Predicate";
import { useCallback, useEffect, useState } from "react";

const LOCAL_STORAGE_KEYS = {
  marketsView: "",
  selectedWallet: "",
  selectedProtocolInstance: "",
};

export const useLocalStorage = <T>(
  key: keyof typeof LOCAL_STORAGE_KEYS,
  defaultValue: T
) => {
  const [value, setValue] = useState<T>(() =>
    getFromLocalStorage(key, defaultValue)
  );

  const remove = useCallback(() => {
    try {
      localStorage.removeItem(key), [key];
      setValue(defaultValue);
    } catch (err) {
      console.warn(`Could not clear "${key}" from local storage`, err);
    }
  }, [key, defaultValue]);

  useEffect(() => {
    if (!isNullable(value)) {
      setToLocalStorage(key, value);
    }
  }, [key, value]);

  return [value, setValue, remove] as const;
};

export const getFromLocalStorage = <T>(key: string, defaultValue: T): T => {
  // could throw if localStorage is undefined in server envs or if JSON.parse fails
  try {
    const value = localStorage.getItem(key);

    if (isNullable(value)) return defaultValue;

    return JSON.parse(value) as T;
  } catch (err) {
    return defaultValue;
  }
};

export const setToLocalStorage = <T>(key: string, value: T): void => {
  const serializedValue = JSON.stringify(value);

  if (typeof window !== "undefined") {
    localStorage.setItem(key, serializedValue);
  }
};
